import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TEAL_500} from '@wandb/weave/common/css/color.styles';
import {Button, Checkbox} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useMoveRunDrawerProjectQuery} from '../../../generated/graphql';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {useDispatch} from '../../../state/hooks';
import * as PollingActions from '../../../state/polling/actions';
import {useViewer} from '../../../state/viewer/hooks';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import type * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import * as ViewHooks from '../../../state/views/hooks';
import {Banner, BannerFlexWrapper} from '../../Banner';
import {GroupContent} from './GroupContent';
import {Body, Container, Footer, Header, Progress} from './MoveRunDrawerPieces';
import {useTrackRunsWithLocation} from './moveRunsEvents';
import {useRunGroups} from './useRunGroups';
import {type RunToMove, useRunMover} from './useRunMover';

export type RunDrawerRun = RunToMove & {
  group?: string | undefined | null;
  displayName?: string | undefined | null;
  user?: {username?: string | undefined | null} | undefined | null;
};

type MoveRunDrawerContents = {
  entityName: string;
  onClose: () => void;
  onMovedRuns: () => void;
  projectName: string;
  runs: RunDrawerRun[];
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  selectedCount: number;
};

export function MoveRunDrawerContents({
  entityName,
  onClose,
  onMovedRuns,
  projectName,
  runs,
  groupSelectionsRef,
  selectedCount,
}: MoveRunDrawerContents) {
  const isInReport = useIsInLoadedReportContext();
  const dispatch = useDispatch();
  const {
    groups,
    loading,
    refetch: refetchRunGroups,
  } = useRunGroups(entityName, projectName);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [manuallyAddedGroups, setManuallyAddedGroups] = useState<string[]>([]);
  const [shouldRefetchGroups, setShouldRefetchGroups] = useState(false);
  const [isCreatingFirstGroup, setIsCreatingFirstGroup] = useState(false);
  const [moveRuns, moveRunState] = useRunMover({
    groupName: selectedGroup,
    entityName,
    projectName,
  });
  const trackMoveRunsWithLocation = useTrackRunsWithLocation();
  const groupSelections = ViewHooks.usePart(groupSelectionsRef);
  const setGrouping = ViewHooks.useViewAction(
    groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );
  const isGroupedByGroup = groupSelections.grouping.some(
    g => g.section === 'run' && g.name === 'group'
  );
  const [isGroupByGroupChecked, setIsGroupByGroupChecked] = useState(false);
  const allRunsInSameGroupName = useMemo(() => {
    if (runs.length === 0) {
      return undefined;
    }
    const firstGroupName = runs[0].group;
    const allSameGroup = runs.every(r => r.group === firstGroupName);
    return allSameGroup ? firstGroupName : undefined;
  }, [runs]);

  const handleSwitchToGroupByGroup = () => {
    setGrouping([...groupSelections.grouping, {section: 'run', name: 'group'}]);
  };

  const runsSelectedGreaterThanLimit = selectedCount > runs.length;

  useEffect(() => {
    if (moveRunState.status === 'complete') {
      const numMoved = moveRunState.results.filter(
        r => r.status === 'success'
      ).length;

      const extraText = runsSelectedGreaterThanLimit
        ? `${selectedCount - runs.length} not moved due to selection limits.`
        : '';

      if (!isGroupedByGroup && !isGroupByGroupChecked) {
        toast(
          <span>
            Moved {numMoved} {maybePluralizeWord(numMoved, 'run')} to{' '}
            {selectedGroup}. {extraText}
            <div
              style={{color: TEAL_500}}
              role="button"
              onClick={e => {
                e.preventDefault();
                handleSwitchToGroupByGroup();
              }}>
              Group runs
            </div>
          </span>
        );
      } else {
        toast(
          `Moved ${numMoved} ${maybePluralizeWord(
            numMoved,
            'run'
          )} to ${selectedGroup}. ${extraText}`
        );
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [moveRunState.status]);

  const onMoveRuns = async () => {
    await moveRuns(runs);
    // send poll tick to trigger runs data refetches (e.g. RSR, RSDQ, bRSDQ)
    // so the change is reflected in the relevant UIs (run selector, charts)
    dispatch(PollingActions.tick());

    if (shouldRefetchGroups) {
      await refetchRunGroups();
      setShouldRefetchGroups(false);
    }
    onMovedRuns();
    if (isGroupByGroupChecked) {
      handleSwitchToGroupByGroup();
    }
  };

  const onCancel = () => {
    if (moveRunState.status === 'running') {
      // Clicking cancel while the move is in progress cancels outstanding runs
      // if the user hits ok on the confirm dialog.
      const response = confirm(
        `Clicking 'Ok' will stop moving remaining runs to ${selectedGroup}.`
      );
      if (response) {
        moveRunState.cancel();
      }
    } else {
      onClose();
    }
  };

  const title = `Move ${maybePluralizeWord(
    runs.length,
    'run'
  )} to another group`;

  const moveRunsText = runsSelectedGreaterThanLimit
    ? `Move ${runs.length} out of ${selectedCount} runs to:`
    : `Move ${runs.length} runs to:`;
  const label =
    runs.length === 1 ? `Move ${runs[0].displayName} to:` : moveRunsText;

  const isMovingRuns = moveRunState.status === 'running';
  const onAddItem = useCallback(
    (item: string) => {
      setSelectedGroup(item);
      setManuallyAddedGroups(prev => {
        if (prev.includes(item)) {
          return prev;
        }
        return [item, ...prev];
      });
      setShouldRefetchGroups(true);
      setIsCreatingFirstGroup(false);
    },
    [setSelectedGroup, setManuallyAddedGroups, setShouldRefetchGroups]
  );

  const items = useMemo(() => {
    return [...manuallyAddedGroups, ...groups].map(group => ({
      name: group,
      isDisabled: group === allRunsInSameGroupName,
      disabledMessage:
        group === allRunsInSameGroupName
          ? 'Selected runs are already in this group'
          : undefined,
    }));
  }, [manuallyAddedGroups, groups, allRunsInSameGroupName]);

  const hasExistingGroups = groups.length > 0 || manuallyAddedGroups.length > 0;
  const viewer = useViewer();
  const {data: projectData} = useMoveRunDrawerProjectQuery({
    variables: {
      entityName,
      projectName,
    },
  });

  const isRunCreator = runs.every(
    run => run.user?.username === viewer?.username
  );
  const hasMultipleCollaborators = (projectData?.project?.totalUsers ?? 0) > 1;
  const showMoveRunInfoBanner = hasMultipleCollaborators && !isRunCreator;

  return (
    <Container isInReport={isInReport}>
      <Header onClose={isMovingRuns ? null : onClose} title={title} />
      <Body>
        {runsSelectedGreaterThanLimit && (
          <Banner variant="warning" className="mb-16">
            <BannerFlexWrapper>
              <Icon name="warning" style={{marginRight: 'auto'}} />
              <div className="inline">
                <span className="inline">
                  Only the first {runs.length} runs will be moved. Try filtering
                  to move additional runs in batches.
                </span>
              </div>
            </BannerFlexWrapper>
          </Banner>
        )}
        {showMoveRunInfoBanner && (
          <Banner variant="info" className="mb-16">
            <BannerFlexWrapper>
              <Icon name="info" style={{marginRight: 'auto'}} />
              <div className="inline">
                <span className="inline">
                  Moving runs between groups affects all project collaborators.
                </span>
              </div>
            </BannerFlexWrapper>
          </Banner>
        )}
        <GroupContent
          loading={loading}
          hasExistingGroups={hasExistingGroups}
          isCreatingFirstGroup={isCreatingFirstGroup}
          isMovingRuns={isMovingRuns}
          items={items}
          label={label}
          selectedGroup={selectedGroup}
          onAddItem={onAddItem}
          setSelectedGroup={setSelectedGroup}
          setIsCreatingFirstGroup={setIsCreatingFirstGroup}
          trackMoveRunsWithLocation={trackMoveRunsWithLocation}
        />
      </Body>
      {!isGroupedByGroup && (
        <div className="align-center flex gap-10 px-24 pb-10">
          <Checkbox
            checked={isGroupByGroupChecked}
            onCheckedChange={() => {
              setIsGroupByGroupChecked(prev => !prev);
            }}
          />
          Apply grouping once runs are moved
        </div>
      )}
      {isMovingRuns && moveRunState.total > 10 ? (
        <Footer>
          <p className="grow">
            Moving run {moveRunState.progress} of {moveRunState.total}
          </p>
          <p>
            {Math.round((moveRunState.progress / moveRunState.total) * 100)}%
          </p>
          <Progress value={moveRunState.progress} max={moveRunState.total} />
        </Footer>
      ) : null}
      <Footer>
        <Button
          variant="secondary"
          data-test="cancel-move-runs"
          className="w-full"
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          icon={isMovingRuns ? 'loading' : undefined}
          aria-label={'Move runs'}
          data-test="move-runs"
          disabled={isMovingRuns || !selectedGroup}
          className="w-full"
          onClick={() => onMoveRuns()}>
          {isMovingRuns ? 'Moving...' : 'Move'}
        </Button>
      </Footer>
    </Container>
  );
}
